<template>
  <div>
    <HrPageLayout
      title="Trainings Catalog"
      subtitle="Manage training courses and certifications"
      icon="📚"
      :breadcrumb-items="[
        { label: 'Home', route: '/dashboard' },
        { label: 'Training & Development' },
        { label: 'Trainings Catalog' }
      ]"
    >
      <template #header-actions>
        <button type="button" class="hr-btn hr-btn-primary" @click="openAdd">
          <i class="bx bx-plus"></i> Add Training
        </button>
      </template>

      <div class="certifications-content">
        <div v-if="message" class="hr-alert" :class="messageSuccess ? 'hr-alert-success' : 'hr-alert-danger'">
          {{ message }}
          <button type="button" class="hr-btn hr-btn-sm" @click="message = ''">&times;</button>
        </div>

        <div class="hr-chart-card">
          <div class="hr-chart-header hr-flex-between">
            <div class="hr-search-wrap">
              <input v-model="searchKeyword" class="hr-input" type="text" placeholder="Search trainings..." @input="loadTrainings">
              <i class="bx bx-search-alt search-icon"></i>
            </div>
            <div class="hr-filter-group">
              <select v-model="filterType" @change="loadTrainings" class="hr-select" style="min-width: 140px;">
                <option value="">All types</option>
                <option value="internal">Internal</option>
                <option value="external">External</option>
                <option value="online">Online</option>
                <option value="certification">Certification</option>
                <option value="workshop">Workshop</option>
                <option value="seminar">Seminar</option>
              </select>
            </div>
          </div>
          <div class="hr-chart-body">
            <div v-if="loading" class="hr-loading-wrap">
              <div class="hr-spinner"></div>
              <p class="hr-text-muted">Loading trainings...</p>
            </div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Category</th>
                    <th>Provider</th>
                    <th>Duration</th>
                    <th>Cost</th>
                    <th>Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(t, idx) in trainings" :key="t.trainingID">
                    <td>{{ idx + 1 }}</td>
                    <td>{{ t.trainingCode }}</td>
                    <td>{{ t.trainingName }}</td>
                    <td><span class="hr-badge hr-badge-info">{{ t.trainingType }}</span></td>
                    <td>{{ t.category || '—' }}</td>
                    <td>{{ t.provider || '—' }}</td>
                    <td>{{ t.duration_days ? t.duration_days + 'd' : '' }} {{ t.duration_hours ? t.duration_hours + 'h' : '—' }}</td>
                    <td>{{ t.cost != null ? (t.currency || 'KES') + ' ' + t.cost : '—' }}</td>
                    <td><span class="hr-badge" :class="t.is_active == 1 ? 'hr-badge-success' : 'hr-badge-secondary'">{{ t.is_active == 1 ? 'Yes' : 'No' }}</span></td>
                    <td>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="openEdit(t)"><i class="bx bx-edit"></i></button>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" @click="confirmDelete(t)"><i class="bx bx-trash"></i></button>
                    </td>
                  </tr>
                  <tr v-if="trainings.length === 0">
                    <td colspan="10" class="hr-text-center hr-py-4 hr-text-muted">No trainings found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </HrPageLayout>

    <!-- Add/Edit Modal -->
    <div class="hr-modal-overlay" v-if="showModal" @click.self="showModal = false">
      <div class="hr-modal hr-modal-medium">
        <div class="hr-modal-header">
          <h3>{{ editingId ? 'Edit Training' : 'Add Training' }}</h3>
          <button type="button" class="hr-modal-close" @click="showModal = false">&times;</button>
        </div>
        <form @submit.prevent="saveTraining" class="hr-form-grid hr-p-3">
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Training Code</label>
            <input v-model="form.trainingCode" class="hr-input" placeholder="e.g. TR-SAFETY01">
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Training Name <span class="hr-text-danger">*</span></label>
            <input v-model="form.trainingName" class="hr-input" required placeholder="Training name">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Type</label>
            <select v-model="form.trainingType" class="hr-select">
              <option value="internal">Internal</option>
              <option value="external">External</option>
              <option value="online">Online</option>
              <option value="certification">Certification</option>
              <option value="workshop">Workshop</option>
              <option value="seminar">Seminar</option>
            </select>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Category</label>
            <input v-model="form.category" class="hr-input" placeholder="Category">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Provider</label>
            <input v-model="form.provider" class="hr-input" placeholder="Provider">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Duration (days / hours)</label>
            <div class="hr-flex gap-2">
              <input v-model.number="form.duration_days" class="hr-input" type="number" min="0" placeholder="Days">
              <input v-model.number="form.duration_hours" class="hr-input" type="number" min="0" placeholder="Hours">
            </div>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Cost</label>
            <input v-model.number="form.cost" class="hr-input" type="number" step="0.01" min="0" placeholder="0">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Currency</label>
            <input v-model="form.currency" class="hr-input" placeholder="KES">
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Description</label>
            <textarea v-model="form.description" class="hr-textarea" rows="2" placeholder="Description"></textarea>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Mandatory</label>
            <input type="checkbox" v-model="form.is_mandatory" :true-value="1" :false-value="0">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Active</label>
            <input type="checkbox" v-model="form.is_active" :true-value="1" :false-value="0">
          </div>
          <div class="hr-modal-footer span-full">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showModal = false">Cancel</button>
            <button type="submit" class="hr-btn hr-btn-primary" :disabled="saving">
              <span v-if="saving" class="hr-spinner small"></span>
              <i v-else class="bx bx-save"></i> Save
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete confirm -->
    <div class="hr-modal-overlay" v-if="deleteTarget" @click.self="deleteTarget = null">
      <div class="hr-modal hr-modal-small">
        <div class="hr-modal-header">
          <h3>Delete Training</h3>
          <button type="button" class="hr-modal-close" @click="deleteTarget = null">&times;</button>
        </div>
        <div class="hr-p-3">
          <p>Delete "{{ deleteTarget && deleteTarget.trainingName }}"? This cannot be undone.</p>
          <div class="hr-mt-3 hr-flex gap-2">
            <button type="button" class="hr-btn hr-btn-secondary" @click="deleteTarget = null">Cancel</button>
            <button type="button" class="hr-btn hr-btn-danger" @click="doDelete" :disabled="deleting">Delete</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "TrainingsCatalog",
  components: { HrPageLayout },
  data() {
    return {
      trainings: [],
      loading: false,
      message: '',
      messageSuccess: false,
      searchKeyword: '',
      filterType: '',
      showModal: false,
      editingId: 0,
      saving: false,
      form: {
        trainingCode: '',
        trainingName: '',
        trainingType: 'internal',
        category: '',
        provider: '',
        duration_days: null,
        duration_hours: null,
        cost: null,
        currency: 'KES',
        description: '',
        is_mandatory: 0,
        is_active: 1
      },
      deleteTarget: null,
      deleting: false
    };
  },
  mounted() {
    this.loadTrainings();
  },
  methods: {
    async loadTrainings() {
      this.loading = true;
      this.message = '';
      const data = new FormData();
      data.append("function", "getTrainings");
      if (this.filterType) data.append("trainingType", this.filterType);
      data.append("activeOnly", "0");
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          let list = res.data.data.trainings || [];
          const q = (this.searchKeyword || '').toLowerCase();
          if (q) {
            list = list.filter(t =>
              (t.trainingName && t.trainingName.toLowerCase().includes(q)) ||
              (t.trainingCode && t.trainingCode.toLowerCase().includes(q)) ||
              (t.category && t.category.toLowerCase().includes(q))
            );
          }
          this.trainings = list;
        }
      } catch (e) {
        this.message = e.message || 'Failed to load trainings';
        this.messageSuccess = false;
      } finally {
        this.loading = false;
      }
    },
    openAdd() {
      this.editingId = 0;
      this.form = {
        trainingCode: '',
        trainingName: '',
        trainingType: 'internal',
        category: '',
        provider: '',
        duration_days: null,
        duration_hours: null,
        cost: null,
        currency: 'KES',
        description: '',
        is_mandatory: 0,
        is_active: 1
      };
      this.showModal = true;
    },
    openEdit(t) {
      this.editingId = t.trainingID;
      this.form = {
        trainingCode: t.trainingCode || '',
        trainingName: t.trainingName || '',
        trainingType: t.trainingType || 'internal',
        category: t.category || '',
        provider: t.provider || '',
        duration_days: t.duration_days ?? null,
        duration_hours: t.duration_hours ?? null,
        cost: t.cost != null ? t.cost : null,
        currency: t.currency || 'KES',
        description: t.description || '',
        is_mandatory: t.is_mandatory ?? 0,
        is_active: t.is_active != null ? t.is_active : 1
      };
      this.showModal = true;
    },
    async saveTraining() {
      this.saving = true;
      this.message = '';
      const data = new FormData();
      data.append("function", "saveTraining");
      data.append("trainingID", this.editingId);
      data.append("trainingCode", this.form.trainingCode);
      data.append("trainingName", this.form.trainingName);
      data.append("trainingType", this.form.trainingType);
      data.append("category", this.form.category);
      data.append("provider", this.form.provider);
      data.append("duration_days", this.form.duration_days || 0);
      data.append("duration_hours", this.form.duration_hours || 0);
      if (this.form.cost != null && this.form.cost !== '') data.append("cost", this.form.cost);
      data.append("currency", this.form.currency);
      data.append("description", this.form.description);
      data.append("is_mandatory", this.form.is_mandatory ? '1' : '0');
      data.append("is_active", this.form.is_active ? '1' : '0');
      data.append("createdBy", sessionStorage.getItem("sessionNames") || '');
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.message = res.data.message || 'Saved successfully';
          this.messageSuccess = true;
          this.showModal = false;
          this.loadTrainings();
        } else {
          this.message = res.data.message || 'Save failed';
          this.messageSuccess = false;
        }
      } catch (e) {
        this.message = e.message || 'Save failed';
        this.messageSuccess = false;
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(t) {
      this.deleteTarget = t;
    },
    async doDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      const data = new FormData();
      data.append("function", "deleteTraining");
      data.append("trainingID", this.deleteTarget.trainingID);
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.message = 'Training deleted';
          this.messageSuccess = true;
          this.deleteTarget = null;
          this.loadTrainings();
        } else {
          this.message = res.data.message || 'Delete failed';
          this.messageSuccess = false;
        }
      } catch (e) {
        this.message = e.message || 'Delete failed';
        this.messageSuccess = false;
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.certifications-content { padding: 0; }
.hr-alert { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-radius: 12px; margin-bottom: 16px; }
.hr-alert-success { background: #d1fae5; color: #065f46; }
.hr-alert-danger { background: #fee2e2; color: #991b1b; }
.hr-flex.gap-2 { display: flex; gap: 8px; }
.hr-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.hr-modal { background: #fff; border-radius: 16px; max-width: 96vw; max-height: 90vh; overflow-y: auto; }
.hr-modal-medium { width: 560px; }
.hr-modal-small { width: 400px; }
.hr-modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e2e8f0; }
.hr-modal-close { background: none; border: none; font-size: 24px; cursor: pointer; color: #64748b; }
.hr-modal-footer { display: flex; gap: 12px; justify-content: flex-end; margin-top: 16px; }
.span-half { grid-column: span 1; }
.span-full { grid-column: 1 / -1; }
.hr-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.hr-p-3 { padding: 20px; }
.hr-mt-3 { margin-top: 16px; }
</style>
